import React from 'react';
import styled from '@emotion/styled';
import { Type, Palette, Download, Check } from 'lucide-react';

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto 2rem;
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  padding: 0.5rem 0;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar for cleaner look */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    justify-content: flex-start;
    padding: 0.5rem 1rem;
  }
`;

const StepContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;

  @media (max-width: 480px) {
    gap: 0.25rem;
    padding: 0.25rem;
  }
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 0.95rem;
  min-height: 44px; /* Minimum touch target size */

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    gap: 0.4rem;
  }

  background: ${({ active, completed }) =>
    completed
      ? 'rgba(34, 197, 94, 0.2)' // Green glow
      : active
        ? 'rgba(124, 58, 237, 0.3)' // Purple pill
        : 'rgba(255, 255, 255, 0.05)'};

  border: 1px solid
    ${({ active, completed }) =>
      completed
        ? 'rgba(34, 197, 94, 0.4)'
        : active
          ? 'rgba(124, 58, 237, 0.6)'
          : 'rgba(255, 255, 255, 0.15)'};

  color: ${({ active, completed }) =>
    completed
      ? '#22c55e'
      : active
        ? '#FFFFFF'
        : '#D1D5DB'}; // gray-300 fallback

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
`;

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: ${({ active, completed }) =>
    completed ? '#22c55e' : active ? '#7c3aed' : 'rgba(255, 255, 255, 0.2)'};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    font-size: 0.8rem;
  }
`;

const StepConnector = styled.div`
  width: 24px;
  height: 2px;
  background: ${({ completed }) =>
    completed ? '#22c55e' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 1px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 16px;
  }

  @media (max-width: 480px) {
    width: 12px;
  }
`;

const QRStepIndicator = ({ currentStep }) => {
  const steps = [
    {
      number: 1,
      title: 'Enter Content',
      icon: Type,
      completed: currentStep > 1,
    },
    {
      number: 2,
      title: 'Customize Design',
      icon: Palette,
      completed: currentStep > 2,
    },
    {
      number: 3,
      title: 'Download',
      icon: Download,
      completed: currentStep > 3,
    },
  ];

  return (
    <StepIndicator>
      <StepContainer>
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <Step
              active={currentStep === step.number}
              completed={step.completed}
            >
              <StepNumber
                active={currentStep === step.number}
                completed={step.completed}
              >
                {step.completed ? <Check size={12} /> : step.number}
              </StepNumber>
              <span>{step.title}</span>
            </Step>
            {index < steps.length - 1 && (
              <StepConnector completed={step.completed} />
            )}
          </React.Fragment>
        ))}
      </StepContainer>
    </StepIndicator>
  );
};

export default QRStepIndicator;
